'use client';

import React, { Suspense } from 'react';
import { Layout, LayoutSidebarSettings } from '@benzinga/core-ui';
import { useBenzingaEdge } from '@benzinga/edge';
import { ScreenerPageHeader } from './ScreenerPageHeader';
import EdgeSignUpBanner from '../../../../src/components/Screener/EdgeSignUpBanner';
import { ScreenerCom } from '@benzinga/screener-widget';
import { CallToAction } from '@benzinga/ui';
import { BENZINGA_ORIGIN } from '../screenerMeta';
import {
  SCREENER_CAMPAIGNS,
  SCREENER_CTA,
  SCREENER_PRESENTED_BY,
  SCREENER_PRESENTED_BY_LINK,
  SCREENER_TRADE,
} from './screenerSponsor';

// const ScreenerCom = React.lazy(() =>
//   import('@benzinga/screener-widget').then(module => ({ default: module.ScreenerCom })),
// );

const FreestarAd = React.lazy(() =>
  import('@benzinga/ads').then(module => ({ default: module.FreestarAd })),
);

const FloatingPopup = React.lazy(() =>
  import('@benzinga/money').then(module => ({ default: module.FloatingPopup })),
);

const AD_FALLBACK = <div className="h-[250px] w-full mt-5" />;

const sidebarSettings: LayoutSidebarSettings = {
  offsetTop: 212,
  visibleOnMobile: true,
  width: 300,
};

const headerConfig = {
  title: 'Stock Screener',
  description:
    'Use the Stock Screener to filter stocks and ETFs based on price change, market cap, dividend yield, earnings per share, and more.',
  url: `${BENZINGA_ORIGIN}/screener`,
  breadcrumbs: [
    { href: '/tools', id: 'tools', name: 'Tools' },
    { href: '/screener', id: 'screener', name: 'Stock Screeners' },
  ],
  presentedBy: SCREENER_PRESENTED_BY,
  presentedByLink: SCREENER_PRESENTED_BY_LINK,
  tabs: [
    { href: '/screener/best-momentum-stocks', key: 'best-rankings', label: 'Best Rankings' },
    { href: '/screener/top-momentum-stocks', key: 'top-rankings', label: 'Top Rankings' },
  ],
};

export const ScreenerPageContent = ({ screenerCollection }: { screenerCollection?: React.ReactNode }) => {
  const { hasEdgeSubscription, isEdgeSubscriptionLoading, isLoggedIn } = useBenzingaEdge();
  const hasEdgeAccess = isLoggedIn && hasEdgeSubscription;
  const hideUpsell = hasEdgeAccess || isEdgeSubscriptionLoading;

  return (
    <div className="screener-page">
      <Layout
        layoutHeader={
          <div className="screener-header-wrapper">
            <ScreenerPageHeader {...headerConfig} />
          </div>
        }
        layoutHeaderOptions={{ full_width: true }}
        layoutMain={
          <>
            <ScreenerCom hasBzEdge={hasEdgeAccess} trade={SCREENER_TRADE} />
            <div className="mt-2">
              <CallToAction {...SCREENER_CTA} />
            </div>
            {screenerCollection}
          </>
        }
        layoutSidebar={
          <>
            {!hideUpsell && <EdgeSignUpBanner isLoggedIn={isLoggedIn} adType="stock-screener" pageUrl="screener" />}
            <Suspense fallback={AD_FALLBACK}>
              <FreestarAd onlyDesktop placement="sidebar_1" />
              <FreestarAd className="w-full" onlyMobile placement="belowcontent" />
            </Suspense>
          </>
        }
        hideSidebar={hasEdgeAccess}
        sidebarSettings={sidebarSettings}
        className="screener-layout"
      />
      <Suspense fallback={null}>
        <FloatingPopup targets={SCREENER_CAMPAIGNS} />
      </Suspense>
    </div>
  );
};
